import React from 'react';
import { Eye, X } from 'lucide-react';
import type { CRMUser } from '../../types';
import type { StaffPortalPreviewKind } from '../../utils/staffPortalPreview';

export type StaffPortalViewAsTarget = {
  kind: StaffPortalPreviewKind;
  uid: string;
};

type Props = {
  designers: CRMUser[];
  architects: CRMUser[];
  engineers: CRMUser[];
  current: StaffPortalViewAsTarget | null;
  currentName?: string | null;
  onChange: (next: StaffPortalViewAsTarget) => void;
  onExit?: () => void;
};

function optionValue(kind: StaffPortalPreviewKind, uid: string): string {
  return `${kind}:${uid}`;
}

function parseOption(value: string): StaffPortalViewAsTarget | null {
  const sep = value.indexOf(':');
  if (sep <= 0) return null;
  const kind = value.slice(0, sep) as StaffPortalPreviewKind;
  const uid = value.slice(sep + 1);
  if ((kind !== 'architect' && kind !== 'designer' && kind !== 'engineer') || !uid) return null;
  return { kind, uid };
}

const StaffPortalViewAs: React.FC<Props> = ({
  designers,
  architects,
  engineers,
  current,
  currentName,
  onChange,
  onExit,
}) => {
  const selectValue = current ? optionValue(current.kind, current.uid) : '';
  const label = current
    ? currentName || current.uid
    : '';

  return (
    <div className={current ? 'ap-view-as-banner' : 'ap-view-as-picker'}>
      <Eye size={14} />
      {current && (
        <span>
          Previewing as <strong>{label}</strong>
        </span>
      )}
      <select
        aria-label="View as"
        value={selectValue}
        onChange={(e) => {
          const next = parseOption(e.target.value);
          if (next) onChange(next);
        }}
      >
        {!current && <option value="">View as…</option>}
        {designers.length > 0 && (
          <optgroup label="Designers">
            {designers.map((u) => (
              <option key={u.uid} value={optionValue('designer', u.uid)}>{u.displayName}</option>
            ))}
          </optgroup>
        )}
        {architects.length > 0 && (
          <optgroup label="Architects">
            {architects.map((u) => (
              <option key={u.uid} value={optionValue('architect', u.uid)}>{u.displayName}</option>
            ))}
          </optgroup>
        )}
        {engineers.length > 0 && (
          <optgroup label="Engineers">
            {engineers.map((u) => (
              <option key={u.uid} value={optionValue('engineer', u.uid)}>{u.displayName}</option>
            ))}
          </optgroup>
        )}
      </select>
      {current && onExit && (
        <button
          type="button"
          className="ap-view-as-clear"
          onClick={onExit}
          title="Exit preview"
        >
          <X size={14} />
          <span>Exit preview</span>
        </button>
      )}
    </div>
  );
};

export default StaffPortalViewAs;
